import { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Phone, ShieldCheck } from "lucide-react";
import { getPostBySlug, BLOG_POSTS, SITE } from "@/lib/blog";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = useMemo(() => getPostBySlug(slug), [slug]);

  useEffect(() => {
    if (!post) return;
    window.scrollTo({ top: 0, behavior: "instant" });

    const prevTitle = document.title;
    document.title = `${post.title} — TexasBusinessDispute.com`;

    // Meta description
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", post.description);

    // Keywords meta
    let kw = document.querySelector('meta[name="keywords"]');
    const kwCreated = !kw;
    if (kwCreated) {
      kw = document.createElement("meta");
      kw.setAttribute("name", "keywords");
      document.head.appendChild(kw);
    }
    const prevKw = kw.getAttribute("content");
    kw.setAttribute("content", (post.keywords || []).join(", "));

    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    const canonCreated = !canon;
    if (canonCreated) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    const prevCanon = canon.getAttribute("href");
    canon.setAttribute("href", `${SITE.url}/blog/${post.slug}`);

    // JSON-LD: Article
    const article = document.createElement("script");
    article.type = "application/ld+json";
    article.setAttribute("data-blog-jsonld", "article");
    article.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Person", name: SITE.attorney, affiliation: SITE.legalEntity },
      publisher: {
        "@type": "LegalService",
        name: SITE.legalEntity,
        url: SITE.url,
        areaServed: "Texas",
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}/blog/${post.slug}` },
      keywords: (post.keywords || []).join(", "),
      articleSection: post.category,
    });
    document.head.appendChild(article);

    // JSON-LD: FAQ (from post)
    let faq = null;
    if (post.faqSchema) {
      faq = document.createElement("script");
      faq.type = "application/ld+json";
      faq.setAttribute("data-blog-jsonld", "faq");
      faq.textContent = JSON.stringify(post.faqSchema);
      document.head.appendChild(faq);
    }

    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
      if (kwCreated) kw.remove();
      else if (prevKw !== null) kw.setAttribute("content", prevKw);
      if (canonCreated) canon.remove();
      else if (prevCanon !== null) canon.setAttribute("href", prevCanon);
      article.remove();
      if (faq) faq.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const idx = BLOG_POSTS.findIndex((p) => p.slug === post.slug);
  const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

  return (
    <div data-testid="blog-post-page">
      {/* Announcement */}
      <div className="ann-bar" data-testid="announcement-bar">
        Attorney advertisement · Prefcap Law (Houston, Texas) &nbsp;·&nbsp;
        <Link to="/#intake">Begin your assessment →</Link>
      </div>

      {/* Nav */}
      <nav className="nav" data-testid="main-nav">
        <div className="container-tx nav-inner">
          <Link to="/" className="brand" data-testid="brand-logo" style={{ textDecoration: "none" }}>
            <div className="brand-mark">tx</div>
            <div>
              <div className="brand-name">TexasBusinessDispute.com</div>
              <div className="brand-sub">Prefcap Law · Houston, Texas</div>
            </div>
          </Link>
          <div className="nav-links">
            <Link className="nav-link" to="/#how" data-testid="nav-how">Process</Link>
            <Link className="nav-link" to="/#practice" data-testid="nav-practice">Practice</Link>
            <Link className="nav-link" to="/blog" data-testid="nav-blog">Insights</Link>
          </div>
          <div className="nav-actions">
            <a className="nav-phone" href="tel:+17132912875" data-testid="nav-phone-cta">
              <Phone size={13} /> (713) 291-2875
            </a>
            <Link className="nav-cta" to="/#intake" data-testid="nav-cta-btn">
              Free Case Assessment <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="blog-article" data-testid="blog-article">
        <div className="container-tx blog-article-inner">
          <Link to="/blog" className="blog-back" data-testid="blog-back-link">
            <ArrowLeft size={13} /> All insights
          </Link>

          <div className="blog-article-meta">
            <span className="blog-list-cat">{post.category}</span>
            <span className="blog-list-date">{formatDate(post.date)}</span>
          </div>

          <h1 className="serif blog-article-title" data-testid="blog-article-title">{post.title}</h1>
          <p className="blog-article-lede">{post.description}</p>

          <div className="blog-article-byline">
            By <strong>{SITE.attorney}</strong> — {SITE.legalEntity}, Houston, Texas
          </div>

          <div className="blog-prose" data-testid="blog-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* In-article CTA */}
          <div className="blog-cta-card" data-testid="blog-inline-cta">
            <div>
              <div className="blog-cta-eyebrow">Have a matter like this?</div>
              <h3 className="serif">Get a confidential assessment.</h3>
              <p>Three minutes. No obligation. A Texas-licensed attorney reviews your intake personally.</p>
            </div>
            <Link className="nav-cta" to="/#intake" data-testid="blog-inline-cta-btn">
              Begin assessment <ArrowRight size={14} />
            </Link>
          </div>

          {/* FAQ rendered for readers */}
          {post.faqSchema && post.faqSchema.mainEntity?.length > 0 && (
            <div className="blog-faq" data-testid="blog-faq">
              <div className="eyebrow copper">Frequently asked</div>
              <h2 className="serif blog-faq-h">Questions readers ask</h2>
              <div className="blog-faq-list">
                {post.faqSchema.mainEntity.map((q, i) => (
                  <details key={i} className="blog-faq-item">
                    <summary>{q.name}</summary>
                    <p>{q.acceptedAnswer?.text}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Next post */}
          {next && next.slug !== post.slug && (
            <Link to={`/blog/${next.slug}`} className="blog-next" data-testid="blog-next-link">
              <div>
                <div className="blog-next-label">Next insight</div>
                <div className="serif blog-next-title">{next.title}</div>
              </div>
              <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </article>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-tx">
          <div className="footer-grid">
            <div>
              <div className="brand" style={{ marginBottom: 14 }}>
                <div className="brand-mark">tx</div>
                <div>
                  <div className="brand-name" style={{ color: "#fbf8f3" }}>TexasBusinessDispute.com</div>
                  <div className="brand-sub">Prefcap Law · Houston, Texas</div>
                </div>
              </div>
              <p>Texas attorney-operated business litigation intake and firm matching service.</p>
              <p style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck size={14} style={{ color: "var(--copper-soft)" }} /> Confidential · No obligation
              </p>
            </div>
            <div>
              <h4>Insights</h4>
              <ul>
                {BLOG_POSTS.map((p) => (
                  <li key={p.slug}><Link style={{ color: "inherit", textDecoration: "none" }} to={`/blog/${p.slug}`}>{p.title}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4>Quick Links</h4>
              <ul>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/#how">How it works</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/#practice">Practice areas</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/blog">Insights</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/admin/login" data-testid="footer-admin-link">Admin</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p style={{ marginBottom: 12 }}>
              Referral fee disclosure. TexasBusinessDispute.com is a trade name of Prefcap Law (principal office: Houston, Texas), operated by lead attorney Reza Chawla. If a referral or any other fee is paid, in accordance with the Texas Disciplinary Rules of Professional Conduct (including Rule 1.04(f)), the client will be informed in writing of (i) the identities of the lawyers and firms involved, (ii) the division of fees between them, and (iii) the responsibility each lawyer is assuming. All such terms are documented in the client engagement letter for the matter, and the client's written consent will be obtained before any referral is finalized and before any fee is shared.
            </p>
            © {new Date().getFullYear()} TexasBusinessDispute.com · Prefcap Law (Houston, Texas). Attorney advertisement. This article is general legal information, not legal advice for any specific matter.
          </div>
        </div>
      </footer>
    </div>
  );
}
